/* Duke & Dover — PDP buy-flow stepper (progressive enhancement), v2 (8-05).
 *
 * Reads the Teeinblue personalizer's own DOM and turns it into a West &
 * Willow-style numbered step flow, reordered to lead with the photo:
 *   1. Upload photo  2. Size  3. Frame (format + color, one card)
 *   4. Pet name      5. Review + Add to cart
 * Price-delta badges ("+$30") are computed live from
 * window.teeinblueCampaign.variantsById — never hardcoded.
 *
 * Contract with the widget — deliberately narrow, because Teeinblue is a Vue
 * app that re-renders the whole option tree whenever an option changes:
 *   - never move, wrap, remove or inject DOM inside the widget's Vue tree
 *     (.tee-campaign); we only set data-* attributes on nodes it already
 *     rendered, and re-apply them from a MutationObserver after re-renders;
 *   - the visual reorder is CSS `order` on a flex column — DOM order is
 *     untouched, so Vue's patching and the form's submit order never change;
 *   - never call preventDefault/stopPropagation (except the photo-required
 *     add-to-cart gate, which is fail-open by construction), never touch
 *     form values, never rebind add-to-cart;
 *   - all layout lives in dd-stepper.css, scoped under body[data-dd-stepper].
 *     If anything here throws 3 times we drop that attribute and the page
 *     falls back to the plain stacked widget, fully usable.
 * Injected DOM lives OUTSIDE the Vue tree only: a review/summary card
 * appended to the theme's own <form> (sibling of .tee-campaign, which is the
 * Vue root), and a sticky price bar appended to <body>.
 *
 * STABILITY INVARIANTS (added 8-05 after the mobile "shaking" bug — a
 * render() that fed itself, oscillating forever below step 3):
 *   1. render() must be idempotent. Running it twice on an unchanged widget
 *      must produce zero DOM changes. If a render can change what the NEXT
 *      render sees, that's a loop, not a bug you can debounce away.
 *   2. Anything this script hides is still part of the flow. collectSteps()
 *      measures visibility, so a card we collapsed must not read as "gone" —
 *      see hiddenByUs(). This was the actual oscillator.
 *   3. Our own DOM writes never re-enter the MutationObserver — see
 *      ourOwnMutation(). The review card lives inside the watched subtree.
 *   4. Scroll never triggers render(); it only moves the fixed sticky bar,
 *      once per frame, via transform. No scroll handler may change layout.
 *   5. Every visibility flip goes through one setter that writes only on a
 *      real change of state, with a hysteresis band around the threshold.
 */
(function () {
  'use strict';

  var WRAP = '.tee-form-wrapper';
  var STEP_SELECTOR = [
    '.tee-available-products',
    '.tee-variants .tee-option',
    '.tee-customization-form > .tee-field',
  ].join(', ');

  /* widget labels we rename; anything else keeps its own wording */
  var TITLES = {
    'tee-available-products': 'Frame',
    'tee-option--color': 'Frame color',
    'tee-option--size': 'Size',
    'tee-option--style': 'Style',
  };

  /* Printful base names are far too long for the format chips and truncate in
     the Review row ("Enhanced Matte Paper Framed Pos…"). Teeinblue rewrites the
     Shopify option values on every campaign Update, so renaming them store-side
     never sticks — the short name has to live here. Keyed lowercase, after the
     "(in)" suffix is stripped. Anything unlisted keeps its own wording, so the
     canvas products are untouched. */
  var FORMATS = {
    'enhanced matte paper framed poster': 'Framed',
    'enhanced matte paper poster': 'Print only',
  };

  var errors = 0;
  var dead = false; /* set by teardown() — nothing may re-decorate the page after */
  var state = {
    touched: {}, // step key -> true once the shopper has answered it
    cursor: 0, // index (among main steps) of the step we're showing open
    pinned: null, // step key the shopper clicked open manually
    booted: false,
  };
  var bar = null;
  var panel = null;
  var panelSig = '';
  var syncTimer = null;
  var observing = false;
  var mo = null;

  /* ---------------------------------------------------------------- utils */

  function visible(el) {
    if (!el) return false;
    if (el.classList.contains('sr-only')) return false;
    if (el.hasAttribute('hidden')) return false;
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  /* Is this node boxless because *we* collapsed it, or because the widget
     dropped it? The distinction is load-bearing.
     dd-stepper.css hides one whole card: [data-dd-sub][data-dd-state='todo']
     (the frame-color row, while the Frame step hasn't been reached). Once
     hidden it measures as invisible, so collectSteps() dropped it, render()
     then saw it as a dead node and stripped its attributes, the CSS stopped
     applying, it reappeared — and the next render hid it again. Forever.
     That flip is the mobile "shaking" bug (the row sits directly under
     step 3, so every card below it moved on every cycle).
     A card *we* hid is still part of the flow and keeps its attributes; the
     widget's own hiding (sr-only / [hidden] / an inline display:none, which
     is how Teeinblue drops frame color on unframed products) still counts as
     gone, so the stale-attribute cleanup below keeps working. */
  function hiddenByUs(el) {
    if (!el || !el.isConnected) return false;
    if (el.classList.contains('sr-only')) return false;
    if (el.hasAttribute('hidden')) return false;
    if (el.style && el.style.display === 'none') return false;
    return el.getAttribute('data-dd-sub') === '1' && !!el.getAttribute('data-dd-state');
  }

  function keyFor(el) {
    if (el.id) return el.id;
    var cls = el.className || '';
    var m = /tee-option--[a-z0-9-]+/i.exec(cls) || /tee-available-products/.exec(cls);
    return m ? m[0] : cls.split(/\s+/).slice(0, 2).join('-');
  }

  function titleFor(el, key) {
    if (TITLES[key]) return TITLES[key];
    var cls = (el.className || '').split(/\s+/);
    for (var i = 0; i < cls.length; i++) {
      if (TITLES[cls[i]]) return TITLES[cls[i]];
    }
    var raw = el.querySelector(':scope > .tee-option__title');
    var text = raw ? (raw.textContent || '').trim() : '';
    if (!text) return 'Choose an option';
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  function kindOf(el) {
    if (el.querySelector('.tee-photo')) return 'photo';
    /* Teeinblue "Additional option" swatch pickers (the Art Pop background
       colour chooser) render as .tee-clipart tiles driven by hidden
       checkbox inputs — no .tee-radio, no <select>, so without this branch
       kindOf() returned 'other' and collectSteps() dropped the field. It
       then rendered raw, outside the numbered cards. Must come before the
       text/select checks: the tiles carry sr-only labels. */
    if (el.querySelector('.tee-clipart')) return 'clipart';
    if (el.querySelector('.tee__input--text, textarea')) return 'text';
    if (el.querySelector('.tee-field__select, select')) return 'select';
    if (el.querySelector('.tee-radio')) return 'radio';
    return 'other';
  }

  function isColorKey(key) {
    return key.indexOf('tee-option--color') === 0;
  }
  function isSizeKey(key) {
    return key.indexOf('tee-option--size') === 0;
  }

  /* visual rank — MUST mirror the `order` values in dd-stepper.css */
  function rankOf(step) {
    if (step.kind === 'photo') return 10;
    if (isSizeKey(step.key)) return 20;
    if (step.key === 'tee-available-products') return 30;
    if (isColorKey(step.key)) return 31;
    if (step.kind === 'clipart') return 35;
    if (step.kind === 'text') return 40;
    return 38;
  }

  /* the human-readable answer for a step, or '' if unanswered */
  function answerOf(el, kind) {
    var node;
    if (kind === 'radio') {
      node = el.querySelector('.tee-radio.active') || el.querySelector('input:checked');
      if (!node) return '';
      if (node.tagName === 'INPUT') {
        var lbl = node.id && el.querySelector('label[for="' + CSS.escape(node.id) + '"]');
        return lbl ? (lbl.textContent || '').trim() : node.value || 'Selected';
      }
      return (node.textContent || '').trim();
    }
    if (kind === 'photo') {
      if (el.querySelector('.tee-photo img, .tee-photo-head img, .tee-photo canvas')) {
        return 'Photo added';
      }
      var file = el.querySelector('input[type="file"]');
      if (file && file.files && file.files.length) return 'Photo added';
      return '';
    }
    if (kind === 'clipart') {
      var tile = el.querySelector('.tee-clipart.active');
      if (!tile) return '';
      var lbl = tile.querySelector('.tee-clipart-label__text');
      var t = lbl ? (lbl.textContent || '').trim() : '';
      return t || (tile.getAttribute('title') || '').trim() || 'Selected';
    }
    if (kind === 'text') {
      var input = el.querySelector('.tee__input--text, textarea');
      return input && input.value ? input.value.trim() : '';
    }
    if (kind === 'select') {
      var sel = el.querySelector('select');
      if (!sel || !sel.value) return '';
      var opt = sel.options[sel.selectedIndex];
      return opt ? (opt.textContent || '').trim() : 'Selected';
    }
    return '';
  }

  function cleanLabel(s) {
    var out = String(s || '').replace(/\s*\(in\)\s*$/i, '').trim();
    return FORMATS[out.toLowerCase()] || out;
  }

  /* ------------------------------------------------------------- the model */

  function collectSteps() {
    var wrap = document.querySelector(WRAP);
    if (!wrap) return [];
    var nodes = wrap.querySelectorAll(STEP_SELECTOR);
    var steps = [];
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (!visible(el) && !hiddenByUs(el)) continue;
      var kind = kindOf(el);
      if (kind === 'other') continue;
      var key = keyFor(el);
      steps.push({
        el: el,
        key: key,
        kind: kind,
        title: titleFor(el, key),
        answer: answerOf(el, kind),
        domIdx: i,
      });
    }
    steps.sort(function (a, b) {
      var d = rankOf(a) - rankOf(b);
      return d !== 0 ? d : a.domIdx - b.domIdx;
    });
    /* frame color folds into the Frame step (one decision card) whenever the
       format toggle exists — it becomes a sub-row, not its own numbered step */
    var hasFormat = false;
    for (var f = 0; f < steps.length; f++) {
      if (steps[f].key === 'tee-available-products') hasFormat = true;
    }
    for (var s = 0; s < steps.length; s++) {
      steps[s].sub = hasFormat && isColorKey(steps[s].key);
    }
    return steps;
  }

  function mainSteps(steps) {
    var out = [];
    for (var i = 0; i < steps.length; i++) if (!steps[i].sub) out.push(steps[i]);
    return out;
  }

  function headerOf(step) {
    return (
      step.el.querySelector(':scope > .tee-option__title') ||
      step.el.querySelector(':scope > .tee-field__header')
    );
  }

  /* index of the first main step the shopper hasn't answered *and touched* */
  function firstOpen(mains) {
    for (var i = 0; i < mains.length; i++) {
      if (!state.touched[mains[i].key] || !mains[i].answer) return i;
    }
    return mains.length - 1;
  }

  /* ------------------------------------------------- price data + deltas */

  function variantList() {
    var c = window.teeinblueCampaign;
    if (!c || !c.variantsById) return null;
    var arr = [];
    for (var k in c.variantsById) {
      if (Object.prototype.hasOwnProperty.call(c.variantsById, k)) {
        var v = c.variantsById[k];
        if (v && typeof v.price === 'number') arr.push(v);
      }
    }
    return arr.length ? arr : null;
  }

  function optionsOf(v) {
    if (v.options && v.options.length) return v.options;
    var o = [];
    if (v.option1) o.push(v.option1);
    if (v.option2) o.push(v.option2);
    if (v.option3) o.push(v.option3);
    return o;
  }

  /* cheapest variant whose options include every value in req */
  function priceFor(vs, req) {
    var best = null;
    for (var i = 0; i < vs.length; i++) {
      var opts = optionsOf(vs[i]);
      var ok = true;
      for (var r = 0; r < req.length; r++) {
        if (req[r] && opts.indexOf(req[r]) < 0) {
          ok = false;
          break;
        }
      }
      if (ok && (best === null || vs[i].price < best)) best = vs[i].price;
    }
    return best;
  }

  function chipsOf(groupEl) {
    var radios = groupEl.querySelectorAll('.tee-radio');
    var out = [];
    for (var i = 0; i < radios.length; i++) {
      var r = radios[i];
      var label = r.querySelector('.tee-radio-label');
      var input = r.querySelector('input');
      var value =
        (input && input.type === 'radio' && input.value && /[a-z″×"]/i.test(input.value)
          ? input.value
          : '') ||
        (label ? (label.textContent || '').trim() : '') ||
        (r.getAttribute('title') || '').trim();
      out.push({
        el: r,
        label: label,
        value: value,
        active: r.classList.contains('active'),
      });
    }
    return out;
  }

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
  function deltaText(cents) {
    var d = cents / 100;
    return '+$' + (d % 1 ? d.toFixed(2) : d.toFixed(0));
  }

  function findStep(steps, pred) {
    for (var i = 0; i < steps.length; i++) if (pred(steps[i])) return steps[i];
    return null;
  }

  function activeValue(groupEl) {
    if (!groupEl) return '';
    var cs = chipsOf(groupEl);
    for (var i = 0; i < cs.length; i++) if (cs[i].active) return cs[i].value;
    return '';
  }

  /* set data-dd-delta badges on size + format chips; data-dd-label cleans the
     "(in)" suffix on format chips. Attribute writes only. */
  function applyDeltas(steps) {
    var vs = variantList();
    var wrap = document.querySelector(WRAP);
    if (!wrap) return;

    var fmtStep = findStep(steps, function (s) {
      return s.key === 'tee-available-products';
    });
    var colorStep = findStep(steps, function (s) {
      return isColorKey(s.key);
    });
    var sizeStep = findStep(steps, function (s) {
      return isSizeKey(s.key);
    });

    /* size chips display smallest-first (8×10 leftmost — Jack 8-05).
       Teeinblue orders sizes by its catalog's creation order and exposes no
       reorder UI, so we set flex `order` by area instead: visual only, the
       widget's own selection state and values are untouched. Runs before the
       price-data guard so the order holds even when variant data is absent. */
    if (sizeStep) {
      var ochips = chipsOf(sizeStep.el);
      for (var so = 0; so < ochips.length; so++) {
        var mm = /(\d+)\D+(\d+)/.exec(ochips[so].value || '');
        if (mm) {
          var ord = String(parseInt(mm[1], 10) * parseInt(mm[2], 10));
          if (ochips[so].el.style.order !== ord) ochips[so].el.style.order = ord;
        }
      }
    }

    /* clean format chip labels regardless of price data */
    if (fmtStep) {
      var fchips = chipsOf(fmtStep.el);
      for (var c = 0; c < fchips.length; c++) {
        var span = fchips[c].label && fchips[c].label.querySelector('span');
        if (span) {
          var clean = cleanLabel(span.textContent);
          if (span.getAttribute('data-dd-label') !== clean) {
            span.setAttribute('data-dd-label', clean);
          }
        }
      }
    }

    /* drop stale badges, then re-apply */
    var old = wrap.querySelectorAll('[data-dd-delta]');
    var keep = [];
    if (!vs) {
      for (var o = 0; o < old.length; o++) old[o].removeAttribute('data-dd-delta');
      return;
    }

    var curFmt = fmtStep ? activeValue(fmtStep.el) : '';
    var curColor = colorStep ? activeValue(colorStep.el) : '';
    var curSize = sizeStep ? activeValue(sizeStep.el) : '';

    function setBadge(chip, delta) {
      var target = chip.label || chip.el;
      if (delta > 0) {
        var txt = deltaText(delta);
        if (target.getAttribute('data-dd-delta') !== txt) {
          target.setAttribute('data-dd-delta', txt);
        }
        keep.push(target);
      }
    }

    if (sizeStep) {
      var schips = chipsOf(sizeStep.el);
      var prices = [];
      var base = null;
      for (var i = 0; i < schips.length; i++) {
        var p = priceFor(vs, [curFmt, curColor, schips[i].value]);
        prices.push(p);
        if (p !== null && (base === null || p < base)) base = p;
      }
      if (base !== null) {
        for (var j = 0; j < schips.length; j++) {
          if (prices[j] !== null) setBadge(schips[j], prices[j] - base);
        }
      }
    }

    if (fmtStep) {
      var fchips2 = chipsOf(fmtStep.el);
      var fprices = [];
      var fbase = null;
      for (var m = 0; m < fchips2.length; m++) {
        /* color left free: all frame colors share a price */
        var fp = priceFor(vs, [fchips2[m].value, curSize]);
        fprices.push(fp);
        if (fp !== null && (fbase === null || fp < fbase)) fbase = fp;
      }
      if (fbase !== null) {
        for (var n = 0; n < fchips2.length; n++) {
          if (fprices[n] !== null) setBadge(fchips2[n], fprices[n] - fbase);
        }
      }
    }

    for (var q = 0; q < old.length; q++) {
      if (keep.indexOf(old[q]) < 0) old[q].removeAttribute('data-dd-delta');
    }
  }

  /* --------------------------------------------------------------- render */

  function render() {
    if (dead) return; /* torn down — never re-decorate the page */
    var steps = collectSteps();
    if (!steps.length) return;
    var mains = mainSteps(steps);

    /* pinning the color sub-row means pinning the Frame step */
    var pinnedKey = state.pinned;
    if (pinnedKey && isColorKey(pinnedKey)) {
      for (var pc = 0; pc < mains.length; pc++) {
        if (mains[pc].key === 'tee-available-products') pinnedKey = mains[pc].key;
      }
    }

    var openIdx = firstOpen(mains);
    if (pinnedKey) {
      for (var p = 0; p < mains.length; p++) {
        if (mains[p].key === pinnedKey) {
          openIdx = p;
          break;
        }
      }
    }
    state.cursor = openIdx;

    /* a group can disappear (frame color only exists on Framed Canvas) — drop
       any styling left on nodes that are no longer part of the flow */
    var live = {};
    for (var k = 0; k < steps.length; k++) live[steps[k].key] = true;
    var stale = document.querySelectorAll(WRAP + ' [data-dd-step], ' + WRAP + ' [data-dd-sub]');
    for (var q = 0; q < stale.length; q++) {
      if (live[stale[q].getAttribute('data-dd-key')]) continue;
      stale[q].removeAttribute('data-dd-step');
      stale[q].removeAttribute('data-dd-state');
      stale[q].removeAttribute('data-dd-key');
      stale[q].removeAttribute('data-dd-sub');
      stale[q].removeAttribute('data-dd-joined');
    }

    var doneCount = 0;
    var modeByKey = {};
    for (var i = 0; i < mains.length; i++) {
      var s = mains[i];
      var answered = !!s.answer && !!state.touched[s.key];
      if (answered) doneCount++;
      var mode = i === openIdx ? 'open' : answered ? 'done' : 'todo';
      modeByKey[s.key] = mode;

      if (s.el.getAttribute('data-dd-step') !== String(i + 1)) {
        s.el.setAttribute('data-dd-step', String(i + 1));
      }
      if (s.el.getAttribute('data-dd-state') !== mode) {
        s.el.setAttribute('data-dd-state', mode);
      }
      s.el.setAttribute('data-dd-key', s.key);
      /* the "add your photo" nudge clears itself once a photo lands */
      if (s.kind === 'photo' && s.answer && s.el.hasAttribute('data-dd-warn')) {
        s.el.removeAttribute('data-dd-warn');
      }

      var head = headerOf(s);
      if (head) {
        if (head.getAttribute('data-dd-num') !== String(i + 1)) {
          head.setAttribute('data-dd-num', String(i + 1));
        }
        if (head.classList.contains('tee-option__title')) {
          if (head.getAttribute('data-dd-title') !== s.title) {
            head.setAttribute('data-dd-title', s.title);
          }
        } else if (mode === 'done' && s.answer && s.kind !== 'photo') {
          /* photo steps keep their thumbnail on screen, so no echo needed */
          if (head.getAttribute('data-dd-value') !== s.answer) {
            head.setAttribute('data-dd-value', s.answer);
          }
        } else if (head.hasAttribute('data-dd-value')) {
          head.removeAttribute('data-dd-value');
        }
        head.setAttribute('data-dd-clickable', '1');
      }

      /* step hints (rendered by CSS while the step is open) */
      if (s.kind === 'photo') {
        var heading = s.el.querySelector('.tee-field__heading');
        if (heading) {
          heading.setAttribute(
            'data-dd-hint',
            'One clear photo of your dog — you’ll see a free preview before you buy.'
          );
        }
      }
      if (s.kind === 'clipart') {
        var bheading = s.el.querySelector('.tee-field__heading');
        if (bheading) {
          bheading.setAttribute('data-dd-hint', 'The colour behind your dog — pick either, same price');
        }
      }
      if (s.kind === 'text') {
        var theading = s.el.querySelector('.tee-field__heading');
        if (theading) {
          theading.setAttribute(
            'data-dd-hint',
            'Added to the portrait exactly as you type it · optional'
          );
        }
      }
    }

    /* the frame-color sub-row mirrors the Frame step's state */
    var joinedFmt = null;
    for (var t = 0; t < steps.length; t++) {
      if (!steps[t].sub) continue;
      var sub = steps[t];
      var parentMode = modeByKey['tee-available-products'] || 'todo';
      sub.el.setAttribute('data-dd-sub', '1');
      sub.el.setAttribute('data-dd-key', sub.key);
      if (sub.el.getAttribute('data-dd-state') !== parentMode) {
        sub.el.setAttribute('data-dd-state', parentMode);
      }
      sub.el.removeAttribute('data-dd-step');
      var shead = headerOf(sub);
      if (shead) {
        shead.setAttribute('data-dd-title', sub.title);
        shead.setAttribute('data-dd-clickable', '1');
        shead.removeAttribute('data-dd-num');
      }
      /* only join the cards when the sub-row is actually on screen — a todo
         Frame step hides its color row, and a joined card with nothing under
         it just looks broken */
      if (parentMode !== 'todo') joinedFmt = true;
    }
    var fmtEl = document.querySelector(WRAP + ' .tee-available-products');
    if (fmtEl) {
      if (joinedFmt) fmtEl.setAttribute('data-dd-joined', '1');
      else fmtEl.removeAttribute('data-dd-joined');
    }

    applyDeltas(steps);
    updatePanel(steps, mains);
    updateBar(mains, doneCount);
  }

  /* Schedule a render. Coalesces the burst of mutations a Vue re-render makes,
     but keeps a leading edge so a long stream of mutations can never starve it
     (a pure trailing debounce left the flow unpainted on first load). */
  var lastRun = 0;
  function sync(delay) {
    if (dead) return;
    var now = Date.now();
    if (now - lastRun > 300) {
      lastRun = now;
      guard(render);
      return;
    }
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      lastRun = Date.now();
      guard(render);
    }, delay == null ? 60 : delay);
  }

  /* scrollIntoView that verifies it actually moved: smooth scrolling is
     silently disabled in some embedded viewports, so retry with 'instant'
     when nothing happened — a blocking step the shopper can't see is a
     dead end */
  function scrollIntoViewSafe(el, block) {
    var before = el.getBoundingClientRect().top;
    try {
      el.scrollIntoView({ behavior: 'smooth', block: block });
    } catch (e) {
      el.scrollIntoView();
      return;
    }
    setTimeout(function () {
      try {
        var after = el.getBoundingClientRect().top;
        if (Math.abs(after - before) >= 4) return;
        if (after >= 0 && after < window.innerHeight * 0.7) return;
        try {
          el.scrollIntoView({ behavior: 'instant', block: block });
        } catch (e2) {
          el.scrollIntoView();
        }
      } catch (e3) {
        /* nothing more we can do */
      }
    }, 400);
  }

  /* explicit window.scrollTo rather than scrollIntoView: the latter is a
     no-op inside some embedded/hidden viewports, and a blocking step the
     shopper can't see is a dead end */
  function scrollElementIntoView(el) {
    if (!el || !el.isConnected) return;
    /* Horizon scrolls a .page-wrapper container, not the window — when the
       document itself has nothing to scroll, window.scrollTo can't help, so
       let scrollIntoView walk the scrollable ancestors instead */
    /* centering a card taller than the viewport pushes its top (and the
       message the shopper needs to read) off-screen — align its top instead */
    var tall = el.offsetHeight > window.innerHeight * 0.8;
    try {
      var doc = document.scrollingElement || document.documentElement;
      if (doc.scrollHeight - doc.clientHeight < 8) {
        scrollIntoViewSafe(el, tall ? 'start' : 'center');
        return;
      }
    } catch (ePre) {
      /* fall through to the window path */
    }
    var start = window.pageYOffset || 0;
    var abs = el.getBoundingClientRect().top + start;
    var target = tall
      ? Math.max(0, abs - 90)
      : Math.max(0, abs - window.innerHeight / 2 + el.offsetHeight / 2);
    if (Math.abs(target - start) < 8) {
      /* the window has nowhere to go — but if the element still isn't on
         screen, an inner container owns the scrolling: hand off */
      var now = el.getBoundingClientRect();
      if (now.top >= 0 && now.top < window.innerHeight * 0.7) return;
      scrollIntoViewSafe(el, tall ? 'start' : 'center');
      return;
    }
    var topBefore = el.getBoundingClientRect().top;
    try {
      window.scrollTo({ top: target, behavior: 'smooth' });
    } catch (e) {
      window.scrollTo(0, target);
    }
    /* some embedded viewports drop smooth scrolling entirely — if the
       element hasn't moved on screen, hand off to the verifying fallback.
       (Checked on the element's own rect: scroll offsets can misreport
       inside emulated viewports, rects don't.) */
    setTimeout(function () {
      try {
        if (Math.abs(el.getBoundingClientRect().top - topBefore) >= 4) return;
        scrollIntoViewSafe(el, tall ? 'start' : 'center');
      } catch (e) {
        /* nothing more we can do */
      }
    }, 350);
  }

  function scrollToStep(idx) {
    var mains = mainSteps(collectSteps());
    var s = mains[idx];
    if (!s) return;
    var r = s.el.getBoundingClientRect();
    var pad = 90;
    if (r.top < pad || r.bottom > window.innerHeight - pad) {
      scrollElementIntoView(s.el);
    }
  }

  /* ------------------------------------------------------------- listeners */

  /* a shopper answered something: mark the step touched and let the flow move on */
  function markTouchedFrom(target) {
    var el = target && target.closest ? target.closest('[data-dd-key]') : null;
    if (!el) return;
    var key = el.getAttribute('data-dd-key');
    if (!key) return;
    var wasPinned = state.pinned;
    state.touched[key] = true;
    /* answering the color sub-row also answers the Frame step */
    if (isColorKey(key)) state.touched['tee-available-products'] = true;
    if (wasPinned === key) state.pinned = null;
    var before = state.cursor;
    sync(80);
    setTimeout(function () {
      guard(function () {
        if (state.cursor !== before) scrollToStep(state.cursor);
      });
    }, 260);
  }

  function onDocClick(e) {
    guard(function () {
      var head = e.target.closest ? e.target.closest('[data-dd-clickable]') : null;
      if (head) {
        var step = head.closest('[data-dd-key]');
        var key = step && step.getAttribute('data-dd-key');
        if (key) {
          /* header click = jump to (or fold) this step. Never blocks the widget:
             these headers are plain labels with no `for` and no input inside. */
          state.pinned = state.pinned === key ? null : key;
          sync(0);
          return;
        }
      }
      /* a collapsed (answered) card reopens from a click ANYWHERE on it —
         the value chip included. The chip's own pointer-events are off in
         this state, so the click lands on the card and gets here; an open
         card never matches (its state is 'open', not 'done'). */
      var doneCard = e.target.closest
        ? e.target.closest('[data-dd-key][data-dd-state="done"]')
        : null;
      if (doneCard) {
        var doneKey = doneCard.getAttribute('data-dd-key');
        if (doneKey) {
          state.pinned = doneKey;
          sync(0);
          return;
        }
      }
      /* a click on an option chip / upload button counts as answering the step */
      if (
        e.target.closest &&
        e.target.closest('.tee-radio, .tee-radio-label, .tee-btn--upload, .tee-photo-label')
      ) {
        markTouchedFrom(e.target);
      }
    });
  }

  function onDocChange(e) {
    guard(function () {
      markTouchedFrom(e.target);
      sync(80);
    });
  }

  function onDocInput(e) {
    guard(function () {
      if (!e.target.closest) return;
      if (!e.target.closest('.tee-field')) return;
      var el = e.target.closest('[data-dd-key]');
      if (el && e.target.value && e.target.value.trim()) {
        state.touched[el.getAttribute('data-dd-key')] = true;
      }
      sync(400); /* let them finish typing before the card restyles */
    });
  }

  /* ------------------------------------------- required-photo gate on ATC */

  /* Teeinblue will happily add to cart with an empty photo layer: it mints a
     customization from the blank artwork and attaches a design link, so the
     order looks fulfillable but has no dog in it. The portal marks the photo
     layer required too (silently) — this is the storefront backstop that
     actually tells the shopper what's missing.

     FAIL-OPEN BY CONSTRUCTION — we only block when the widget is *positively*
     in its empty state: the placeholder box is present AND no uploaded image
     AND no file queued. If Teeinblue ever renames those classes, every check
     misses and the click goes straight through. Anything thrown here also
     lets the click through. A false negative costs a blank order; a false
     positive would cost every sale, so the bias is deliberate. */
  function photoFieldsMissing() {
    var wrap = document.querySelector(WRAP);
    if (!wrap) return [];
    var fields = wrap.querySelectorAll('.tee-customization-form > .tee-field');
    var missing = [];
    for (var i = 0; i < fields.length; i++) {
      var f = fields[i];
      if (!visible(f)) continue;
      var photo = f.querySelector('.tee-photo');
      if (!photo) continue;
      var uploaded = photo.querySelector('.tee-photo-uploaded img, .tee-photo-uploaded canvas');
      var placeholder = photo.querySelector('.tee-photo-placeholder');
      var file = f.querySelector('input[type="file"]');
      var queued = file && file.files && file.files.length;
      if (!uploaded && !queued && placeholder) missing.push(f);
    }
    return missing;
  }

  function warnMissingPhoto(field) {
    guard(function () {
      field.setAttribute('data-dd-warn', "Add your dog's photo to continue");
      /* echo the block right where the click happened — the review card sits
         next to the buttons, so the shopper sees why nothing was added even
         if the auto-scroll is defeated */
      if (panel) panel.setAttribute('data-dd-blocked', '1');
      var key = field.getAttribute('data-dd-key');
      if (key) state.pinned = key;
      sync(0);
      setTimeout(function () {
        guard(function () {
          /* Vue may have swapped the node out since the click — re-find it,
             or a detached rect reads as "visible" and the scroll is skipped */
          var target = field;
          if (!target.isConnected) {
            var again = photoFieldsMissing();
            target = again.length
              ? again[0]
              : document.querySelector(WRAP + ' .tee-field--photo');
          }
          scrollElementIntoView(target);
        });
      }, 120);
    });
  }

  function onAtcCapture(e) {
    var btn = e.target.closest && e.target.closest('.tee-btn--atc, .tee-btn--buynow');
    if (!btn) return;
    var missing;
    try {
      missing = photoFieldsMissing();
    } catch (err) {
      return; /* can't tell -> never stand in the way of a sale */
    }
    if (!missing.length) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    warnMissingPhoto(missing[0]);
  }

  /* ------------------------------------------------------- review summary */

  /* Lives OUTSIDE the Vue tree: appended to the theme's own <form>, as a
     sibling of .tee-campaign (the Vue root). Flex `order` slots it between
     the name step and the widget's real Preview/Add-to-cart block, which is
     restyled by CSS into the card's footer — the real buttons keep doing the
     work, nothing is proxied or hidden. */
  function buildPanel(form) {
    if (panel) return panel;
    panel = document.createElement('div');
    panel.className = 'dd-review';
    panel.innerHTML =
      '<div class="dd-review__head">' +
      '<span class="dd-review__num"></span>' +
      '<span class="dd-review__title">Review your order</span>' +
      '</div>' +
      '<div class="dd-review__rows"></div>' +
      '<div class="dd-review__price"></div>';
    /* tapping the "Not added yet" row jumps to the photo step */
    panel.addEventListener('click', function (e) {
      guard(function () {
        if (!e.target.closest || !e.target.closest('.dd-review__row--todo')) return;
        var missing = photoFieldsMissing();
        var field = missing.length
          ? missing[0]
          : document.querySelector(WRAP + ' .tee-field--photo');
        if (!field) return;
        var key = field.getAttribute('data-dd-key');
        if (key) state.pinned = key;
        sync(0);
        setTimeout(function () {
          guard(function () {
            scrollElementIntoView(field);
          });
        }, 120);
      });
    });
    form.appendChild(panel);
    document.body.setAttribute('data-dd-review', 'on');
    return panel;
  }

  function esc(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function updatePanel(steps, mains) {
    var form = document.querySelector(WRAP);
    if (!form) return;
    buildPanel(form);

    var fmtStep = findStep(steps, function (s) {
      return s.key === 'tee-available-products';
    });
    var colorStep = findStep(steps, function (s) {
      return isColorKey(s.key);
    });
    var sizeStep = findStep(steps, function (s) {
      return isSizeKey(s.key);
    });
    var photoStep = findStep(steps, function (s) {
      return s.kind === 'photo';
    });
    var nameStep = findStep(steps, function (s) {
      return s.kind === 'text';
    });
    var bgStep = findStep(steps, function (s) {
      return s.kind === 'clipart';
    });

    /* the blocked banner clears itself the moment a photo lands */
    if (panel.hasAttribute('data-dd-blocked') && photoStep && photoStep.answer) {
      panel.removeAttribute('data-dd-blocked');
    }

    var rows = [];
    if (photoStep) {
      var thumb = form.querySelector('.tee-photo-uploaded img');
      rows.push({
        label: 'Photo',
        value: photoStep.answer ? 'Added ✓' : 'Not added yet',
        ok: !!photoStep.answer,
        thumb: thumb ? thumb.getAttribute('src') : '',
      });
    }
    if (sizeStep) {
      rows.push({ label: 'Size', value: sizeStep.answer || '—', ok: true });
    }
    if (fmtStep) {
      var fval = cleanLabel(fmtStep.answer || activeValue(fmtStep.el));
      var cval = colorStep && !colorStep.el.classList.contains('sr-only')
        ? (colorStep.answer || activeValue(colorStep.el))
        : '';
      rows.push({
        label: 'Frame',
        value: fval + (cval && cval !== 'Default' ? ' · ' + cval : ''),
        ok: true,
      });
    }
    if (bgStep) {
      rows.push({
        label: 'Background',
        value: bgStep.answer || activeValue(bgStep.el) || '—',
        ok: true,
      });
    }
    if (nameStep) {
      rows.push({
        label: 'Name',
        value: nameStep.answer || 'None · optional',
        ok: true,
      });
    }

    /* price breakdown from the live variant table (never hardcoded) */
    var lines = [];
    var total = null;
    var vs = variantList();
    if (vs) {
      var curFmt = fmtStep ? activeValue(fmtStep.el) : '';
      var curColor = colorStep ? activeValue(colorStep.el) : '';
      var curSize = sizeStep ? activeValue(sizeStep.el) : '';
      total = priceFor(vs, [curFmt, curColor, curSize]);

      /* base = cheapest unframed size; deltas stack on top of it */
      var baseFmt = '';
      if (fmtStep) {
        var fc = chipsOf(fmtStep.el);
        var bp = null;
        for (var b = 0; b < fc.length; b++) {
          var p0 = priceFor(vs, [fc[b].value]);
          if (p0 !== null && (bp === null || p0 < bp)) {
            bp = p0;
            baseFmt = fc[b].value;
          }
        }
      }
      var base = priceFor(vs, baseFmt ? [baseFmt] : []);
      if (base !== null && total !== null) {
        var baseSize = '';
        if (sizeStep) {
          var sc = chipsOf(sizeStep.el);
          for (var z = 0; z < sc.length; z++) {
            var pz = priceFor(vs, baseFmt ? [baseFmt, sc[z].value] : [sc[z].value]);
            if (pz === base && !baseSize) baseSize = sc[z].value;
          }
        }
        lines.push({
          l: cleanLabel(baseFmt || 'Canvas') + (baseSize ? ' — ' + baseSize : ''),
          v: money(base),
        });
        var canvasAtSize =
          curSize && baseFmt ? priceFor(vs, [baseFmt, curSize]) : curSize ? priceFor(vs, [curSize]) : base;
        if (canvasAtSize !== null && canvasAtSize - base > 0) {
          lines.push({ l: 'Larger size — ' + curSize, v: deltaText(canvasAtSize - base) });
        }
        var frameDelta = total - (canvasAtSize === null ? base : canvasAtSize);
        if (frameDelta > 0) {
          lines.push({
            l: (FORMATS[String(curFmt || '').replace(/\s*\(in\)\s*$/i, '').trim().toLowerCase()] ? 'Frame' : 'Floating frame') + (curColor && curColor !== 'Default' ? ' — ' + curColor : ''),
            v: deltaText(frameDelta),
          });
        }
      }
    }
    var totalText = total !== null ? money(total) : '';
    if (!totalText) {
      var priceEl = form.querySelector('.tee-price--current');
      totalText = priceEl ? (priceEl.textContent || '').trim() : '';
    }

    var sig = JSON.stringify([rows, lines, totalText, mains.length]);
    if (sig === panelSig) return;
    panelSig = sig;

    panel.querySelector('.dd-review__num').textContent = String(mains.length + 1);

    var rowsHtml = '';
    for (var i = 0; i < rows.length; i++) {
      rowsHtml +=
        '<div class="dd-review__row' + (rows[i].ok ? '' : ' dd-review__row--todo') + '">' +
        (rows[i].thumb
          ? '<img class="dd-review__thumb" alt="" src="' + esc(rows[i].thumb) + '">'
          : '') +
        '<span class="dd-review__label">' + esc(rows[i].label) + '</span>' +
        '<span class="dd-review__value">' + esc(rows[i].value) + '</span>' +
        '</div>';
    }
    panel.querySelector('.dd-review__rows').innerHTML = rowsHtml;

    var priceHtml = '';
    for (var j = 0; j < lines.length; j++) {
      priceHtml +=
        '<div class="dd-review__line"><span>' + esc(lines[j].l) + '</span><span>' +
        esc(lines[j].v) + '</span></div>';
    }
    if (totalText) {
      priceHtml +=
        '<div class="dd-review__total"><span>Total</span><span>' + esc(totalText) + '</span></div>';
    }
    priceHtml +=
      '<div class="dd-review__note">Free preview · You approve the final proof before we print</div>';
    panel.querySelector('.dd-review__price').innerHTML = priceHtml;
  }

  /* --------------------------------------------------- sticky price bar */

  function buildBar() {
    if (bar) return bar;
    bar = document.createElement('div');
    bar.className = 'dd-sticky-bar';
    bar.innerHTML =
      '<div class="dd-sticky-bar__meta">' +
      '<span class="dd-sticky-bar__price"></span>' +
      '<span class="dd-sticky-bar__progress"></span>' +
      '</div>' +
      '<button type="button" class="dd-sticky-bar__btn">Add to cart</button>';
    bar.querySelector('.dd-sticky-bar__btn').addEventListener('click', function () {
      guard(function () {
        /* only the photo is truly required — optional steps must never trap
           the shopper in a loop. The capture-phase gate double-checks anyway. */
        var missing = photoFieldsMissing();
        if (missing.length) {
          warnMissingPhoto(missing[0]);
          return;
        }
        var atc = document.querySelector('.tee-form-wrapper .tee-btn--atc');
        if (atc) atc.click();
      });
    });
    document.body.appendChild(bar);
    return bar;
  }

  /* keep an eye on the widget's real add-to-cart block; Vue can swap the node
     out on re-render, so re-observe whenever the element identity changes */
  var actionsSeen = null;
  var actionsWatched = null;
  var actionsObserver = null;
  var barVisible = null; /* last value we actually wrote */

  /* one write per real change — a repeated identical setAttribute is still a
     style invalidation, and on mobile a stream of them reads as flicker */
  function setBarVisible(show) {
    if (!bar || barVisible === show) return;
    barVisible = show;
    bar.setAttribute('data-dd-visible', show ? '1' : '0');
  }

  function watchActions(el) {
    if (!el || el === actionsWatched || typeof IntersectionObserver === 'undefined') return;
    if (actionsObserver) actionsObserver.disconnect();
    actionsWatched = el;
    /* deliberately NOT resetting actionsSeen: Vue swaps this node on every
       re-render, and blanking the answer dropped us back onto the rect
       fallback for a beat — with different thresholds than the observer's,
       which flipped the bar hidden -> shown -> hidden. Keep the last answer
       until the fresh observer reports. */
    actionsObserver = new IntersectionObserver(
      function (entries) {
        guard(function () {
          if (dead) return;
          actionsSeen = entries[entries.length - 1].isIntersecting;
          setBarVisible(!actionsSeen);
        });
      },
      { rootMargin: '-40px 0px 0px 0px', threshold: 0 }
    );
    actionsObserver.observe(el);
  }

  /* Rect fallback for the beat before the observer first reports. Hysteretic
     on purpose: the show and hide thresholds are 32px apart, and inside that
     dead band we keep the current answer. A card collapsing or the review
     card re-rendering moves .tee-form-actions by a few pixels — without the
     band that nudge alone could flip the bar, and a flipping bar plus the
     layout it reacts to is exactly how a scroll turns into a shake. */
  function barShouldShow(actions) {
    if (!actions) return true;
    var r = actions.getBoundingClientRect();
    if (r.bottom <= 0) return true; /* scrolled clean past it */
    if (r.top < window.innerHeight - 40) return false; /* clearly on screen */
    if (r.top > window.innerHeight - 8) return true; /* clearly below the fold */
    return barVisible === null ? true : barVisible; /* dead band: hold */
  }

  function raf(fn) {
    if (window.requestAnimationFrame) return window.requestAnimationFrame(fn);
    return setTimeout(fn, 16);
  }

  /* Scroll must never run render(). The old scroll handler called sync(150),
     whose leading edge fired a full render (dozens of layout reads + attribute
     writes) every ~300ms while a finger was on the screen — enough to jank on
     its own, and it re-entered the open/done/todo state machine mid-scroll.
     Scrolling only ever changes one thing: whether the real add-to-cart block
     is on screen. So that's all this touches, once per frame. */
  var barPending = false;
  function refreshBar() {
    if (barPending || dead || !bar) return;
    barPending = true;
    raf(function () {
      barPending = false;
      guard(function () {
        if (dead || !bar) return;
        if (actionsSeen !== null) {
          setBarVisible(!actionsSeen);
          return;
        }
        setBarVisible(barShouldShow(document.querySelector(WRAP + ' .tee-form-actions')));
      });
    });
  }

  function updateBar(mains, doneCount) {
    if (!bar) return;
    var price = document.querySelector('.tee-form-wrapper .tee-price--current');
    var priceText = price ? (price.textContent || '').trim() : '';
    var priceEl = bar.querySelector('.dd-sticky-bar__price');
    if (priceEl.textContent !== priceText) priceEl.textContent = priceText;
    var totalSteps = mains.length + 1; /* + review */
    var ready = false;
    try {
      ready = !photoFieldsMissing().length;
    } catch (e) {
      ready = true;
    }
    var progressText = ready
      ? 'Ready to add'
      : 'Step ' + Math.min(state.cursor + 1, totalSteps) + ' of ' + totalSteps;
    var progressEl = bar.querySelector('.dd-sticky-bar__progress');
    if (progressEl.textContent !== progressText) progressEl.textContent = progressText;

    /* hide it while the widget's own add-to-cart is on screen. An
       IntersectionObserver is the source of truth (scroll events can be
       coalesced away); the hysteretic rect test is the fallback before it
       first fires. Both go through setBarVisible, so the bar only ever moves
       on a real change of answer. */
    var actions = document.querySelector(WRAP + ' .tee-form-actions');
    watchActions(actions);
    if (actionsSeen !== null) setBarVisible(!actionsSeen);
    else setBarVisible(barShouldShow(actions));
  }

  /* ------------------------------------------------------------- failsafe */

  /* The review card lives inside .tee-form-wrapper, which is exactly what the
     MutationObserver watches — so rewriting its rows fed straight back in as
     "the widget changed", re-ran render(), and rewrote the card again. The
     panelSig check kept that to one extra pass in the steady state, but any
     value that isn't perfectly stable (a re-minted thumbnail URL, a price
     that moves with a step appearing/disappearing) turned it into a
     free-running loop with no scrolling required. Our own mutations are not
     news; ignore them. */
  function ourOwnMutation(node) {
    var el = node && node.nodeType === 1 ? node : node && node.parentNode;
    if (!el || !el.closest) return false;
    return !!el.closest('.dd-review');
  }

  function guard(fn) {
    try {
      fn();
    } catch (err) {
      errors++;
      if (window.console) console.warn('[dd-stepper]', err);
      if (errors >= 3) teardown();
    }
  }

  /* strip every trace of the stepper — the widget keeps working, plain stacked */
  function teardown() {
    dead = true; /* first, so nothing in flight can re-decorate behind us */
    try {
      /* stop the machinery too — otherwise the observers kept firing, and
         buildPanel() happily re-created the review card we just removed */
      if (mo) mo.disconnect();
      mo = null;
      if (actionsObserver) actionsObserver.disconnect();
      actionsObserver = null;
      actionsWatched = null;
      actionsSeen = null;
      barVisible = null;
      clearTimeout(syncTimer);
      document.body.removeAttribute('data-dd-stepper');
      document.body.removeAttribute('data-dd-review');
      /* give the widget's add-to-cart back too — a broken stepper must never
         be the reason a shopper can't buy */
      document.removeEventListener('click', onAtcCapture, true);
      if (bar && bar.parentNode) bar.parentNode.removeChild(bar);
      bar = null;
      if (panel && panel.parentNode) panel.parentNode.removeChild(panel);
      panel = null;
      var marked = document.querySelectorAll(
        '[data-dd-step], [data-dd-num], [data-dd-warn], [data-dd-sub], [data-dd-delta], [data-dd-label], [data-dd-hint], [data-dd-joined]'
      );
      for (var i = 0; i < marked.length; i++) {
        marked[i].removeAttribute('data-dd-step');
        marked[i].removeAttribute('data-dd-state');
        marked[i].removeAttribute('data-dd-key');
        marked[i].removeAttribute('data-dd-num');
        marked[i].removeAttribute('data-dd-title');
        marked[i].removeAttribute('data-dd-value');
        marked[i].removeAttribute('data-dd-clickable');
        marked[i].removeAttribute('data-dd-warn');
        marked[i].removeAttribute('data-dd-sub');
        marked[i].removeAttribute('data-dd-delta');
        marked[i].removeAttribute('data-dd-label');
        marked[i].removeAttribute('data-dd-hint');
        marked[i].removeAttribute('data-dd-joined');
      }
    } catch (e) {
      /* nothing left to do */
    }
    observing = false;
  }

  /* ----------------------------------------------------------------- boot */

  function boot() {
    var wrap = document.querySelector(WRAP);
    if (!wrap || !wrap.querySelector('.tee-campaign, .tee-customization-form')) return false;
    if (state.booted) return true;
    state.booted = true;

    document.body.setAttribute('data-dd-stepper', 'on');
    buildBar();

    /* capture phase so it lands before Teeinblue's own add-to-cart handler */
    document.addEventListener('click', onAtcCapture, true);
    document.addEventListener('click', onDocClick, false);
    document.addEventListener('change', onDocChange, false);
    document.addEventListener('input', onDocInput, false);
    /* scroll only ever moves the sticky bar — never a full re-render */
    window.addEventListener('scroll', refreshBar, { passive: true });
    window.addEventListener('resize', function () {
      refreshBar();
      sync(200);
    }, { passive: true });

    if (!observing) {
      observing = true;
      mo = new MutationObserver(function (records) {
        if (dead) return;
        for (var i = 0; i < records.length; i++) {
          if (!ourOwnMutation(records[i].target)) {
            sync(60);
            return;
          }
        }
        /* every record was our own doing — do NOT re-render */
      });
      mo.observe(wrap, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    }

    guard(render);
    /* the widget paints its option groups a beat after the campaign root
       mounts — re-run a few times so the first paint is never missed */
    [400, 1200, 2500, 5000, 9000].forEach(function (ms) {
      setTimeout(function () {
        guard(render);
      }, ms);
    });
    window.addEventListener('load', function () {
      sync(0);
    });
    return true;
  }

  /* the widget mounts async — poll briefly, then give up quietly */
  function waitForWidget() {
    var tries = 0;
    var t = setInterval(function () {
      tries++;
      var ok = false;
      guard(function () {
        ok = boot();
      });
      if (ok || tries > 60) clearInterval(t);
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForWidget);
  } else {
    waitForWidget();
  }
})();
